import { useCallback, useState } from 'react'

type UseExcludedPtuGroupsReturn = {
  excludedPtuGroups: Set<string>
  togglePtuGroup: (groupUuid: string, hour: number) => void
  removeStaleExclusions: (assignedGroupUuids: Set<string>) => void
  resetExclusions: () => void
  isExcluded: (groupUuid: string, hour: number) => boolean
}

function makeKey(groupUuid: string, hour: number): string {
  return `${groupUuid}:${hour}`
}

export function useExcludedPtuGroups(): UseExcludedPtuGroupsReturn {
  const [excludedPtuGroups, setExcludedPtuGroups] = useState<Set<string>>(() => new Set())

  const togglePtuGroup = useCallback((groupUuid: string, hour: number) => {
    setExcludedPtuGroups((prev) => {
      const key = makeKey(groupUuid, hour)
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  const removeStaleExclusions = useCallback((assignedGroupUuids: Set<string>) => {
    setExcludedPtuGroups((prev) => {
      if (prev.size === 0) return prev
      const next = new Set<string>()
      for (const key of prev) {
        const groupUuid = key.slice(0, key.indexOf(':'))
        if (assignedGroupUuids.has(groupUuid)) {
          next.add(key)
        }
      }
      return next.size === prev.size ? prev : next
    })
  }, [])

  const resetExclusions = useCallback(() => {
    setExcludedPtuGroups(new Set())
  }, [])

  const isExcluded = useCallback(
    (groupUuid: string, hour: number) => excludedPtuGroups.has(makeKey(groupUuid, hour)),
    [excludedPtuGroups],
  )

  return { excludedPtuGroups, togglePtuGroup, removeStaleExclusions, resetExclusions, isExcluded }
}
